/**
 * cc-converter.js
 * Currency Converter — single self-contained module.
 * Handles: conversion engine, dropdown UI, mobile widget, checkout notice.
 * Reads window.CurrencyConverter.config (set by cc-settings-bridge.liquid).
 */
(function () {
  'use strict';

  var config = (window.CurrencyConverter && window.CurrencyConverter.config) || {
    baseCurrency: 'JOD',
    currencies: []
  };

  if (!config.currencies || config.currencies.length === 0) return;

  var currentCurrency = config.baseCurrency;
  var STORAGE_KEY = 'cc_selected_currency';

  // ── Conversion Engine ──────────────────────────────────────────────────

  function extractBasePrice(el) {
    var text = el.textContent || '';
    var cleaned = text.replace(/[^\d.]/g, '');
    var parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts.slice(0, parts.length - 1).join('') + '.' + parts[parts.length - 1];
    }
    return parseFloat(cleaned) || 0;
  }

  // Target span.money — this is where Shopify puts the actual price text.
  // Works on product pages, collection pages, cart page, and cart drawer.
  var PRICE_SELECTOR = '.money, .Money';

  function stampPrices() {
    var elements = document.querySelectorAll(PRICE_SELECTOR);
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el.dataset.ccBasePrice) {
        var price = extractBasePrice(el);
        if (price > 0) {
          el.dataset.ccBasePrice = price;
          el.dataset.ccOriginalText = el.textContent;
        }
      }
    }
  }

  function getCurrencyByCode(code) {
    for (var i = 0; i < config.currencies.length; i++) {
      if (config.currencies[i].code === code) return config.currencies[i];
    }
    return null;
  }

  function convertAllPrices() {
    var elements = document.querySelectorAll('[data-cc-base-price]');
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (currentCurrency === config.baseCurrency) {
        el.textContent = el.dataset.ccOriginalText;
      } else {
        var currency = getCurrencyByCode(currentCurrency);
        if (!currency) continue;
        var base = parseFloat(el.dataset.ccBasePrice) || 0;
        var converted = Math.round(base * currency.rate * 2) / 2;
        el.textContent = currency.flag + ' ' + currency.code + ' ' + converted.toFixed(2);
      }
    }
  }

  // ── Persistence ────────────────────────────────────────────────────────

  function saveSelection(code) {
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) {}
  }

  function loadSelection() {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }

  // ── Public API ─────────────────────────────────────────────────────────

  function setCurrency(code) {
    var currency = getCurrencyByCode(code);
    if (!currency && code !== config.baseCurrency) return;
    currentCurrency = code;
    saveSelection(code);
    stampPrices();
    convertAllPrices();
    updateDropdownUI(code);
    updateMobileUI(code);
    updateNoticeUI(code);
    document.dispatchEvent(new CustomEvent('cc:currency-changed', {
      detail: { currency: currency || { code: config.baseCurrency } }
    }));
  }

  // ── Header Dropdown ────────────────────────────────────────────────────

  function updateDropdownUI(activeCode) {
    var flagEl = document.getElementById('cc-selector-flag');
    var codeEl = document.getElementById('cc-selector-code');
    var list = document.getElementById('cc-selector-list');
    if (!flagEl || !codeEl || !list) return;

    var activeCur = getCurrencyByCode(activeCode);
    flagEl.textContent = activeCur ? activeCur.flag : '';
    codeEl.textContent = activeCur ? activeCur.code : config.baseCurrency;

    var options = list.querySelectorAll('.cc-selector__option');
    for (var j = 0; j < options.length; j++) {
      if (options[j].dataset.code === activeCode) {
        options[j].classList.add('cc-selector__option--active');
        options[j].setAttribute('aria-selected', 'true');
      } else {
        options[j].classList.remove('cc-selector__option--active');
        options[j].setAttribute('aria-selected', 'false');
      }
    }
  }

  function initDropdown() {
    var selector = document.getElementById('cc-selector');
    var trigger = document.getElementById('cc-selector-trigger');
    var list = document.getElementById('cc-selector-list');
    if (!selector || !trigger || !list) return;

    // Add JOD (base currency) as the first option.
    var jodLi = document.createElement('li');
    jodLi.setAttribute('role', 'option');
    jodLi.className = 'cc-selector__option';
    jodLi.dataset.code = config.baseCurrency;
    jodLi.innerHTML =
      '<span class="cc-selector__option-flag" aria-hidden="true"></span>' +
      '<span class="cc-selector__option-code">' + config.baseCurrency + '</span>' +
      '<span class="cc-selector__option-name">Base</span>';
    jodLi.addEventListener('click', function () {
      setCurrency(config.baseCurrency);
      selector.classList.remove('cc-selector--open');
      trigger.setAttribute('aria-expanded', 'false');
    });
    list.appendChild(jodLi);

    for (var i = 0; i < config.currencies.length; i++) {
      (function(cur) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.className = 'cc-selector__option';
        li.dataset.code = cur.code;
        li.innerHTML =
          '<span class="cc-selector__option-flag" aria-hidden="true">' + cur.flag + '</span>' +
          '<span class="cc-selector__option-code">' + cur.code + '</span>' +
          '<span class="cc-selector__option-name">' + cur.symbol + '</span>';
        li.addEventListener('click', function () {
          setCurrency(cur.code);
          selector.classList.remove('cc-selector--open');
          trigger.setAttribute('aria-expanded', 'false');
        });
        list.appendChild(li);
      })(config.currencies[i]);
    }

    updateDropdownUI(currentCurrency);
    selector.style.display = '';

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      var open = selector.classList.toggle('cc-selector--open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    document.addEventListener('click', function (e) {
      if (!selector.contains(e.target)) {
        selector.classList.remove('cc-selector--open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // ── Mobile Widget ──────────────────────────────────────────────────────

  function updateMobileUI(activeCode) {
    var fabFlag = document.getElementById('cc-widget-fab-flag');
    var fabCode = document.getElementById('cc-widget-fab-code');
    var list = document.getElementById('cc-widget-list');
    if (!fabFlag || !fabCode) return;

    var activeCur = getCurrencyByCode(activeCode);
    fabFlag.textContent = activeCur ? activeCur.flag : '';
    fabCode.textContent = activeCur ? activeCur.code : config.baseCurrency;

    if (!list) return;
    var options = list.querySelectorAll('.cc-widget-overlay__option');
    for (var j = 0; j < options.length; j++) {
      if (options[j].dataset.code === activeCode) {
        options[j].classList.add('cc-widget-overlay__option--active');
        options[j].setAttribute('aria-selected', 'true');
      } else {
        options[j].classList.remove('cc-widget-overlay__option--active');
        options[j].setAttribute('aria-selected', 'false');
      }
    }
  }

  function initMobile() {
    var fab = document.getElementById('cc-widget-fab');
    var overlay = document.getElementById('cc-widget-overlay');
    var backdrop = document.getElementById('cc-widget-backdrop');
    var closeBtn = document.getElementById('cc-widget-close');
    var list = document.getElementById('cc-widget-list');
    if (!fab || !overlay || !list) return;

    // Add JOD (base currency) as the first option.
    var jodLi = document.createElement('li');
    jodLi.setAttribute('role', 'option');
    jodLi.className = 'cc-widget-overlay__option';
    jodLi.dataset.code = config.baseCurrency;
    jodLi.innerHTML =
      '<span class="cc-widget-overlay__option-flag" aria-hidden="true"></span>' +
      '<span class="cc-widget-overlay__option-code">' + config.baseCurrency + '</span>' +
      '<span class="cc-widget-overlay__option-name">Base</span>';
    jodLi.addEventListener('click', function () {
      setCurrency(config.baseCurrency);
      closeOverlay();
    });
    list.appendChild(jodLi);

    for (var i = 0; i < config.currencies.length; i++) {
      (function(cur) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.className = 'cc-widget-overlay__option';
        li.dataset.code = cur.code;
        li.innerHTML =
          '<span class="cc-widget-overlay__option-flag" aria-hidden="true">' + cur.flag + '</span>' +
          '<span class="cc-widget-overlay__option-code">' + cur.code + '</span>' +
          '<span class="cc-widget-overlay__option-name">' + cur.symbol + '</span>';
        li.addEventListener('click', function () {
          setCurrency(cur.code);
          closeOverlay();
        });
        list.appendChild(li);
      })(config.currencies[i]);
    }

    updateMobileUI(currentCurrency);
    fab.style.display = 'inline-flex';

    function openOverlay() {
      overlay.hidden = false;
      fab.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(function () {
        overlay.classList.add('cc-widget-overlay--open');
      });
    }

    function closeOverlay() {
      overlay.classList.remove('cc-widget-overlay--open');
      fab.setAttribute('aria-expanded', 'false');
      setTimeout(function () { overlay.hidden = true; }, 250);
    }

    fab.addEventListener('click', openOverlay);
    if (closeBtn) closeBtn.addEventListener('click', closeOverlay);
    if (backdrop) backdrop.addEventListener('click', closeOverlay);

    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && !overlay.hidden) {
        closeOverlay();
      }
    });
  }

  // ── Checkout Notice ────────────────────────────────────────────────────

  function updateNoticeUI(currencyCode) {
    var notice = document.getElementById('cc-checkout-notice');
    var textEl = document.getElementById('cc-checkout-notice-text');
    if (!notice || !textEl) return;

    if (!currencyCode || currencyCode === config.baseCurrency) {
      notice.style.display = 'none';
    } else {
      var cartTotal = parseFloat(notice.dataset.cartTotal) || 0;
      if (cartTotal > 0) {
        textEl.textContent = 'You will be charged ' + notice.dataset.cartTotal + ' JOD at checkout';
      } else {
        textEl.textContent = 'You will be charged in JOD at checkout';
      }
      notice.style.display = 'flex';
    }
  }

  // ── Geo-Detection ──────────────────────────────────────────────────────

  var COUNTRY_CURRENCY_MAP = {
    'AE': 'AED', 'SA': 'SAR', 'KW': 'KWD', 'BH': 'BHD', 'QA': 'QAR', 'OM': 'OMR',
    'US': 'USD', 'CA': 'USD',
    'GB': 'GBP',
    'DE': 'EUR', 'FR': 'EUR', 'IT': 'EUR', 'ES': 'EUR', 'NL': 'EUR', 'BE': 'EUR',
    'AT': 'EUR', 'PT': 'EUR', 'IE': 'EUR', 'FI': 'EUR', 'GR': 'EUR',
    'AU': 'AUD', 'EG': 'EGP', 'JO': 'JOD'
  };

  // Default fallback currency for countries not in the map.
  var FALLBACK_CURRENCY = 'USD';

  function applyGeoResult(code) {
    if (code === config.baseCurrency) return;
    if (!getCurrencyByCode(code)) return;
    currentCurrency = code;
    convertAllPrices();
    updateDropdownUI(code);
    updateMobileUI(code);
    updateNoticeUI(code);
  }

  function detectAndApply() {
    fetch('https://api.country.is')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        // If user manually picked a currency while fetch was in-flight, respect that.
        if (loadSelection()) return;
        var country = d.country || null;
        var mapped = country ? COUNTRY_CURRENCY_MAP[country] : null;
        // If country is in the map, use the mapped currency.
        // If country is NOT in the map, fall back to USD.
        var target = mapped || FALLBACK_CURRENCY;
        applyGeoResult(target);
      })
      .catch(function() {
        // Fetch failed — fall back to USD if no saved preference.
        if (loadSelection()) return;
        applyGeoResult(FALLBACK_CURRENCY);
      });
  }

  // ── Dynamic Content Observer ─────────────────────────────────────────

  var observer;
  function observeDynamicContent() {
    var debounceTimer;
    observer = new MutationObserver(function(mutations) {
      // Check if any new .money elements were added.
      var hasNew = false;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1 && (node.classList && node.classList.contains('money') || node.querySelector && node.querySelector('.money'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (!hasNew) return;
      // Debounce — batch rapid DOM changes into one stamp+convert pass.
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        observer.disconnect();
        stampPrices();
        if (currentCurrency !== config.baseCurrency) convertAllPrices();
        observer.observe(document.body, { childList: true, subtree: true });
      }, 100);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Init ───────────────────────────────────────────────────────────────

  function init() {
    stampPrices();

    var saved = loadSelection();
    if (saved && getCurrencyByCode(saved)) {
      currentCurrency = saved;
      convertAllPrices();
    }

    initDropdown();
    initMobile();
    updateNoticeUI(currentCurrency);
    observeDynamicContent();

    // Auto-detect country only if no saved preference exists.
    if (!saved) {
      detectAndApply();
    }
  }

  // Expose public API.
  window.CurrencyConverter = window.CurrencyConverter || {};
  window.CurrencyConverter.initialized = true;
  window.CurrencyConverter.config = config;
  window.CurrencyConverter.setCurrency = setCurrency;
  window.CurrencyConverter.getCurrentCurrency = function () { return currentCurrency; };
  window.CurrencyConverter.getCurrencyByCode = getCurrencyByCode;

  init();

})();
